import { useCallback, useEffect, useRef, useState } from 'react';

import useSidebarStore from '@/components/sidebar/hooks/useSidebarStore';
import useLogger from '@/hooks/useLogger';
import { headerAuthorization, headerBuildNumber } from '@/queries/utils';
import useVerwerkSignaalBericht from '@/state/selectors/useVerwerkSignaalBericht';
import useVerwerkWerklijnEvent from '@/state/selectors/useVerwerkWerklijnEvent';
import { SignaalEvent } from '@/types/Signalen';
import {
  ConflictUpdate,
  ConflictUpdateStatus,
  DefectUpdate,
  DefectUpdateStatus,
  InregelingUpdate,
  UitvoeringsmeldingUpdate,
  UitvoeringsmeldingUpdateStatus,
  WerklijnUpdate,
} from '@/types/sseEvent';
import { useAuth } from '@/utils/auth/context';
import { AuthApi } from '@/utils/auth/types';
import { isSecurityEnabled } from '@/utils/devModeConfig';
import { envVariables } from '@/utils/env';
import { fetchEventSource } from '@microsoft/fetch-event-source';

class PleaseReconnectError extends Error {}

export type SSEConnectionStatus = 'not-yet-initialized' | 'disabled' | 'connecting' | 'connected' | 'disconnected';

/**
 * Custom hook voor het verwerken van Updates via Server Sent Events.
 * Let op: Gebruik deze hook maar op 1 plek binnen een route, omdat anders dubbele subscription
 * onstaat en alle events vaker dan 1 keer worden verwerkt.
 */
export function useServerSentEvents() {
  const log = useLogger();

  const { apiUrlNS, apiUrlProRail, imageBuildNumber, authProvider } = envVariables();
  const { verwerkWerklijnEvent } = useVerwerkWerklijnEvent();
  const { verwerkSignaalBericht } = useVerwerkSignaalBericht();

  const apiUrl = window.location.hostname.includes('prorail') ? apiUrlProRail : apiUrlNS;

  const auth = useAuth();
  const [connectionStatus, setConnectionStatus] = useState<SSEConnectionStatus>('not-yet-initialized');
  const [retryCounter, setRetryCounter] = useState(0);
  const [sseSessionId, setSseSessionId] = useState('not-yet-initialized');

  // We gebruiken een ref voor de access token (JWT) omdat een nieuwe token niet gelijk betekent
  // dat de bestaande SSE-connectie opnieuw opgebouwd hoeft te worden,
  // maar een nieuwe verbinding moet wel altijd de nieuwste waarde gebruiken, vandaar deze ref:
  const authRef = useRef<AuthApi | null>(null);

  useEffect(() => {
    authRef.current = auth ?? null;
  }, [auth]);

  const aliveTimeout = useRef<NodeJS.Timeout | undefined>(undefined);
  const receivedMessage = useCallback(() => {
    if (aliveTimeout.current) {
      clearTimeout(aliveTimeout.current);
    }
    aliveTimeout.current = setTimeout(() => {
      console.warn('Did not receive message for 30 seconds, will restart SSE connection.');
      log('Did not receive message for 30 seconds, will restart SSE connection.');
      // Trigger reinitialization of the Event Source by changing the value for the retry counter
      setRetryCounter((c) => c + 1);
    }, 30_000);

    return () => {
      if (aliveTimeout.current) {
        clearTimeout(aliveTimeout.current);
      }
    };
  }, [log, aliveTimeout]);

  useEffect(() => {
    if (isSecurityEnabled() && !auth) {
      return;
    }
    const controller = new AbortController();
    let localSessionId = 'not-yet-initialized';

    async function subscribeToWerklijnUpdates() {
      console.log('SSE: subscribing');
      const initObj: Omit<RequestInit, 'headers'> & {
        openWhenHidden?: boolean;
        headers?: Record<string, string>;
      } = {
        method: 'GET',
        headers: { ...headerBuildNumber(imageBuildNumber) },
        signal: controller.signal,
        openWhenHidden: true,
      };
      if (isSecurityEnabled()) {
        if (authProvider === 'bff-cookie') {
          if (!auth.isAuthenticated) {
            setConnectionStatus('disconnected');
            // Geen SSE connectie starten — gebruiker is niet ingelogd
            return;
          }
          setConnectionStatus('connecting');
          initObj.credentials = 'include';
        } else {
          const token = await authRef.current?.getAccessToken();
          if (!token) {
            setConnectionStatus('disconnected');
            // Geen SSE connectie starten
            return;
          }
          setConnectionStatus('connecting');
          initObj.headers = {
            ...initObj.headers,
            ...headerAuthorization(token),
          };
        }
      }

      return await fetchEventSource(`${apiUrl}/server-sent-events/werklijn-update`, {
        ...initObj,
        async onopen(res) {
          receivedMessage();
          if (res.ok && res.status === 200) {
            console.log(`SSE: 200 received in onopen[${retryCounter}], waiting for registratie_succesvol`);
            log(`SSE: 200 received in onopen[${retryCounter}], waiting for registratie_succesvol`);
          }
          if (res.status === 401) {
            throw new PleaseReconnectError('401 received in fetch-event-source "onopen" callback');
          } else if (res.status >= 400 && res.status < 500 && res.status !== 429) {
          }
        },
        onmessage(event) {
          receivedMessage();
          if (event.event === 'registratie_succesvol') {
            localSessionId = JSON.parse(event.data);
            console.log('SSE: registratie_succesvol bevestiging ontvangen met sessionId', localSessionId);
            setSseSessionId(localSessionId);
            setConnectionStatus('connected');
          } else if (event.event === 'werklijn_update') {
            const parsedWerklijnUpdate: WerklijnUpdate = JSON.parse(event.data);
            verwerkWerklijnEvent({
              ...parsedWerklijnUpdate,
              timestamp: new Date(),
            });
          } else if (event.event === 'inregeling_update') {
            const parsedInregelingUpdate: InregelingUpdate = JSON.parse(event.data);
            verwerkWerklijnEvent({
              ...parsedInregelingUpdate,
              timestamp: new Date(),
            });
          } else if (event.event === 'defect') {
            // refresh mwd
            const parsedDefectUpdate: DefectUpdate = JSON.parse(event.data);
            verwerkWerklijnEvent({
              ...parsedDefectUpdate,
              status: DefectUpdateStatus,
              timestamp: new Date(),
            });
            // refresh sidepanel
            useSidebarStore.getState().triggerDefectenRefresh();
          } else if (event.event === 'uitvoeringsmelding') {
            const parsedUitvoeringsmeldingUpdate: UitvoeringsmeldingUpdate = JSON.parse(event.data);
            verwerkWerklijnEvent({
              ...parsedUitvoeringsmeldingUpdate,
              status: UitvoeringsmeldingUpdateStatus,
              timestamp: new Date(),
            });
          } else if (event.event === 'conflict') {
            const parsedConflict: ConflictUpdate = JSON.parse(event.data);
            // Op een conflict update event met bron 'ACCEPTEREN' moet de werklijn (mwd) bijgewerkt worden
            if (parsedConflict.bron === 'ACCEPTEREN') {
              verwerkWerklijnEvent({
                ...parsedConflict,
                status: ConflictUpdateStatus,
                timestamp: new Date(),
              });
            }
            // Op enige conflict update moet de conflictenlijst in de sidepanel worden ververst
            // Let wel, de bronnen 'INTERN' en 'EXTERN' zijn nog disabled in de backend maar een refresh
            // wordt wel getriggerd bij een 'ACCEPTEREN' event
            useSidebarStore.getState().triggerConflictenRefresh();
          } else if (event.event === 'signaal') {
            const signaalEvent: SignaalEvent = JSON.parse(event.data);
            verwerkSignaalBericht(signaalEvent);
          } else if (event.event === 'heartbeat') {
            // Nog geen toepassing voor heartbeats
            console.log('SSE: Heartbeat binnengekregen voor sessionId', localSessionId);
          } else {
            console.log('SSE: Onbekend event:', event.event);
          }
        },
        onclose() {
          console.log('SSE: onclose');
          log('SSE: onclose');
          setConnectionStatus('disconnected');
          // We gaan uit van een verbinding die in principe altijd open blijft,
          // daarom gooien we hier een error zodat er opnieuw een verbinding gemaakt wordt.
          throw new PleaseReconnectError('Connection closed, should reconnect');
        },
        onerror(err) {
          setConnectionStatus('disconnected');
          if (err instanceof PleaseReconnectError) {
            console.log(`SSE: Reconnecting due to PleaseReconnectError: [${err.message}]`);
            log(`SSE: Reconnecting due to PleaseReconnectError: [${err.message}]`);
          } else {
            console.error('SSE: Onverwachte fout in fetch-event-source "onerror" callback:', err);
            log(`SSE: Onverwachte fout in fetch-event-source "onerror" callback:${err}`);
          }
          // Always rethrow to restart the entire show,
          // since the automatic reconnect from fetch-event-source won't use updated JWT so is utter useless crap
          throw err;
        },
      });
    }

    async function startListeningToEvents() {
      try {
        await subscribeToWerklijnUpdates();
      } catch (error) {
        console.error(`SSE: Retry ${retryCounter} for session '${localSessionId}'.`, { error });
        log(
          `SSE: Verbinding is verbroken voor sessie '${localSessionId}'. Dit kan gebeuren, Dit betrof verbinding nummer ${retryCounter}.`,
        );
        setTimeout(() => setRetryCounter((c) => c + 1), 5000);
      }
    }
    startListeningToEvents();
    return () => {
      setConnectionStatus('disconnected');
      controller.abort();
    };
  }, [apiUrl, auth.isAuthenticated, retryCounter, verwerkWerklijnEvent]);

  return { connectionStatus, sseSessionId, retryCounter };
}
